import React, { useState, useEffect, CSSProperties } from 'react';

export interface IconGenIconProps {
  /** Icon name as it appears in your IconGen library */
  icon: string;
  /** Your IconGen user ID */
  userId: string;
  /** Icon size preset or custom size */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2x' | '3x' | number | string;
  /** Custom CSS class name */
  className?: string;
  /** Custom inline styles */
  style?: CSSProperties;
  /** Alt text for accessibility */
  alt?: string;
  /** Loading placeholder component */
  fallback?: React.ReactNode;
  /** Error placeholder component */
  errorFallback?: React.ReactNode;
  /** Callback when icon loads successfully */
  onLoad?: () => void;
  /** Callback when icon fails to load */
  onError?: (error: Error) => void;
  /** Base URL for IconGen API (defaults to icongen.pro) */
  baseUrl?: string;
}

const sizeMap = {
  xs: '12px',
  sm: '16px',
  md: '20px',
  lg: '24px',
  xl: '32px',
  '2x': '48px',
  '3x': '64px'
};

export const IconGenIcon: React.FC<IconGenIconProps> = ({
  icon,
  userId,
  size = 'md',
  className = '',
  style = {},
  alt,
  fallback = null,
  errorFallback = null,
  onLoad,
  onError,
  baseUrl = 'https://icongen.pro'
}) => {
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadIcon = async () => {
      try {
        setLoading(true);
        setError(null);

        // Clean icon name for API call
        const cleanIconName = encodeURIComponent(icon);
        const apiUrl = `${baseUrl}/api/icons/${userId}/${cleanIconName}`;

        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`Icon "${icon}" not found (${response.status})`);
        }

        const iconData = await response.json();
        
        if (iconData.public_url) {
          setIconUrl(iconData.public_url);
          onLoad?.();
        } else {
          throw new Error('No icon data available');
        }
        
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to load icon');
        setError(error);
        onError?.(error);
      } finally {
        setLoading(false);
      }
    };

    if (icon && userId) {
      loadIcon();
    }
  }, [icon, userId, baseUrl, onLoad, onError]);

  // Calculate size
  const iconSize = typeof size === 'number' ? `${size}px` : 
                   typeof size === 'string' && size in sizeMap ? sizeMap[size as keyof typeof sizeMap] : 
                   size;

  // Base styles
  const baseStyles: CSSProperties = {
    display: 'inline-block',
    width: iconSize,
    height: iconSize,
    ...style
  };

  // Loading state
  if (loading) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return (
      <div 
        className={`icongen-loading ${className}`}
        style={{
          ...baseStyles,
          backgroundColor: '#f0f0f0',
          borderRadius: '2px',
          animation: 'icongen-pulse 1.5s ease-in-out infinite'
        }}
        aria-label="Loading icon..."
      />
    );
  }

  // Error state
  if (error) {
    if (errorFallback) {
      return <>{errorFallback}</>;
    }
    return (
      <div 
        className={`icongen-error ${className}`}
        style={{
          ...baseStyles,
          backgroundColor: '#ffebee',
          border: '1px solid #ffcdd2',
          borderRadius: '2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '10px',
          color: '#c62828'
        }}
        title={error.message}
        aria-label={`Error loading icon: ${error.message}`}
      >
        ⚠
      </div>
    );
  }

  // Success state
  return (
    <img
      src={iconUrl!}
      alt={alt || icon}
      className={`icongen-icon ${className}`}
      style={baseStyles}
      onError={() => {
        const error = new Error('Failed to load icon image');
        setError(error);
        onError?.(error);
      }}
    />
  );
};

// CSS for loading animation
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes icongen-pulse {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 0.8; }
    }
  `;
  document.head.appendChild(style);
}
